import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { AdminGuard } from 'app/core/auth/guards/admin.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { workspaceRoleGuard } from 'app/core/auth/guards/workspace-role.guard';
import { LayoutComponent } from 'app/layout/layout.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/chat' (conversations)
    {path: '', pathMatch : 'full', redirectTo: 'chat'},

    // Redirect signed-in user to the '/chat' (conversations)
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {path: 'signed-in-redirect', pathMatch : 'full', redirectTo: 'chat'},

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes')},
            {path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes')},
            {path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes')}
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes')},
            {path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes')},
        ]
    },

    // Landing routes (accessible to anyone, including authenticated users)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes')},
            {path: 'accept-invite', loadChildren: () => import('app/modules/auth/accept-invite/accept-invite.routes')},
            {path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes')},
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            {path: 'no-workspace', loadChildren: () => import('app/modules/auth/no-workspace/no-workspace.routes')},
            {path: 'example', loadChildren: () => import('app/modules/admin/example/example.routes')},
            {path: 'users', canActivate: [AdminGuard], loadChildren: () => import('app/modules/admin/users/users.routes')},
            {path: 'workspaces', canActivate: [AdminGuard], loadChildren: () => import('app/modules/admin/workspaces/workspaces.routes')},
            {path: 'api-keys', canActivate: [workspaceRoleGuard(['admin', 'configurator'])], loadChildren: () => import('app/modules/admin/api-keys/api-keys.routes')},
            {path: 'quick-replies', canActivate: [workspaceRoleGuard(['admin', 'supervisor'])], loadChildren: () => import('app/modules/admin/quick-replies/quick-replies.routes')},
            {path: 'tags', canActivate: [workspaceRoleGuard(['admin', 'supervisor', 'configurator', 'agent'])], loadChildren: () => import('app/modules/admin/tags/tags.routes')},
            {path: 'saved-filters', canActivate: [workspaceRoleGuard(['admin', 'supervisor', 'agent'])], loadChildren: () => import('app/modules/admin/saved-filters/saved-filters.routes')},
            {path: 'queues', loadChildren: () => import('app/modules/admin/queues/queues.routes')},
            {path: 'message-quota', canActivate: [workspaceRoleGuard(['admin', 'supervisor', 'configurator', 'agent'])], loadChildren: () => import('app/modules/admin/message-quota/message-quota.routes')},
            {path: 'analytics', canActivate: [workspaceRoleGuard(['admin', 'supervisor'])], loadChildren: () => import('app/modules/admin/analytics/analytics.routes')},
            {path: 'workspace-settings', canActivate: [workspaceRoleGuard(['admin'])], loadChildren: () => import('app/modules/admin/workspace-settings/workspace-settings.routes')},
            {path: 'webhook-events', canActivate: [workspaceRoleGuard(['admin', 'configurator'])], loadChildren: () => import('app/modules/admin/webhook-events/webhook-events.routes')},
            {path: 'sync', canActivate: [workspaceRoleGuard(['admin', 'configurator'])], loadChildren: () => import('app/modules/sync/sync.routes')},
            {path: 'chat', canActivate: [workspaceRoleGuard(['admin', 'supervisor', 'agent'])], loadChildren: () => import('app/modules/chat/chat.routes')},
        ]
    }
];
